// src/js/image.js

/**
 * Rendert ein <picture> Element mit AVIF/WebP/JPG
 * @param {Array} imgArray
 * @param {Object} options
 */
const escapeHtml = (value) =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export function renderImage(imgArray, options = {}) {
    const {
        alt = "",
        sizes = "(max-width: 800px) 100vw, 800px",
        className = "",
        loading = "lazy",
    } = options;

    if (!Array.isArray(imgArray) || imgArray.length === 0) {
        console.warn("renderImage: leeres Bild-Array erhalten");
        return "";
    }

    const avif = imgArray.filter((i) => i.format === "avif");
    const webp = imgArray.filter((i) => i.format === "webp");
    const jpg = imgArray.filter(
        (i) => i.format === "jpg" || i.format === "jpeg"
    );

    const buildSrcSet = (arr) =>
        arr
            .filter((i) => i.src && i.width)
            .map((i) => `${escapeHtml(i.src)} ${i.width}w`)
            .join(", ");

    const sortedImages = [...imgArray]
        .filter((i) => i && typeof i.width === "number")
        .sort((a, b) => a.width - b.width);

    const largest = sortedImages[sortedImages.length - 1] || imgArray[0];
    const fallbackSource =
        jpg.length > 0
            ? jpg[Math.floor(jpg.length / 2)]
            : webp.length > 0
                ? webp[Math.floor(webp.length / 2)]
                : avif.length > 0
                    ? avif[Math.floor(avif.length / 2)]
                    : imgArray[0];

    const sourceTags = [
        avif.length
            ? `<source type="image/avif" srcset="${buildSrcSet(avif)}" sizes="${escapeHtml(
                sizes
            )}">`
            : "",
        webp.length
            ? `<source type="image/webp" srcset="${buildSrcSet(webp)}" sizes="${escapeHtml(
                sizes
            )}">`
            : "",
    ]
        .filter(Boolean)
        .join("\n      ");

    const width = fallbackSource.width || largest.width || 1;
    const height = fallbackSource.height || largest.height || 1;

    return `
    <picture${className ? ` class="${escapeHtml(className)}"` : ""}>
      ${sourceTags}
      <img
        src="${escapeHtml(fallbackSource.src)}"
        alt="${escapeHtml(alt)}"
        loading="${escapeHtml(loading)}"
        width="${width}"
        height="${height}"
        style="aspect-ratio: ${width}/${height}"
      >
    </picture>
  `;
}

/**
 * Holt Daten aus imageMap (kompatibel alt + neu)
 */
function resolveImageData(entry, el) {
    // 👉 neuer Stil (mit { src, alt })
    if (entry && entry.src) {
        return {
            srcArray: entry.src,
            alt: el.dataset.alt || entry.alt || "",
        };
    }

    // 👉 alter Stil (nur Array)
    return {
        srcArray: entry,
        alt: el.dataset.alt || "",
    };
}

/**
 * Normales Mounten (alle Bilder)
 */
export function mountImages(imageMap) {
    document.querySelectorAll("[data-img]").forEach((el) => {
        const key = el.dataset.img;

        if (!imageMap[key]) {
            console.warn(`Image "${key}" nicht gefunden`);
            return;
        }

        const { srcArray, alt } = resolveImageData(imageMap[key], el);

        const sizes =
            el.dataset.sizes ||
            (el.classList.contains("hero-bild")
                ? "(max-width: 800px) 100vw, 800px"
                : el.classList.contains("")
                    ? "(max-width: 640px) 96vw, (max-width: 767px) 600px, 46vw"
                    : "(max-width: 640px) 96vw, (max-width: 767px) 600px, 46vw");

        const className = el.dataset.class || "";
        const loading = el.dataset.loading || "lazy";

        el.innerHTML = renderImage(srcArray, {
            alt,
            sizes,
            className,
            loading,
        });
    });
}

/**
 * Lazy Mount nur für Slider.
 *
 * Unterstützt jetzt den generischen Datenattribut-Selektor
 * `[data-lazy-slider]` sowie die bestehenden Klassen.
 */
export function lazyMountImages(imageMap) {
    const sliders = document.querySelectorAll(
        "[data-lazy-slider], .jagd-slider, .food-slider"
    );

    const observer = new IntersectionObserver(
        (entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                mountImagesInContainer(entry.target, imageMap);
                obs.unobserve(entry.target);
            });
        },
        {
            rootMargin: "200px",
        }
    );

    sliders.forEach((slider) => observer.observe(slider));
}

/**
 * Mount nur innerhalb Container
 */
function mountImagesInContainer(container, imageMap) {
    container.querySelectorAll("[data-img]").forEach((el) => {
        const key = el.dataset.img;

        if (!imageMap[key]) {
            console.warn(`Image "${key}" nicht gefunden`);
            return;
        }

        const { srcArray, alt } = resolveImageData(imageMap[key], el);

        const sizes =
            el.dataset.sizes ||
            "(max-width: 640px) 96vw, (max-width: 767px) 600px, 46vw";

        const className = el.dataset.class || "";
        const loading = el.dataset.loading || "lazy";

        el.innerHTML = renderImage(srcArray, {
            alt,
            sizes,
            className,
            loading,
        });
    });
}
